import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  fetchLevels,
  fetchStudents,
  fetchAssignments,
  fetchPaperAssignmentRecords,
  fetchTeacherStudentsOnlineAssignments,
  deletePaperAssignmentRecord,
  generateQRCode,
  downloadAssignment,
  downloadQRPdf,
  clearStudents,
  clearQRCode,
  clearError
} from "../paperAssignmentSlice";
import ClassNoticeModal from "./ClassNoticeModal";
import HomeworkFilesModal from "./HomeworkFilesModal";
import ClassContentModal from "./ClassContentModal";
import NotificationSettings from "./NotificationSettings";
import MacroGuideModal from "./MacroGuideModal";
import ExampleTemplateModal from "./ExampleTemplateModal";
import QRCoverGenerator from "./QRCoverGenerator";
import QRBulkUploadModal from "./QRBulkUploadModal";
import PaperAssignmentCalendar from "./PaperAssignmentCalendar";
// import QRCodeSection from "./QRCodeSection";
// import QRCodeBulkSendButton from "./QRCodeBulkSendButton";
import { notificationAPI } from "../../notification/notificationAPI";
import apiClient from "../../../services/apiClient";

const PaperAssignmentPage = () => {
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);
  const {
    levels,
    students,
    assignments,
    paperAssignmentRecords,
    qrCodeData,
    loading,
    error
  } = useSelector((state) => state.paperAssignment);

  const [finishedOnlineAssignments, setFinishedOnlineAssignments] = React.useState([]);
  
  const [selectedLevel, setSelectedLevel] = useState("");
  const [selectedStudent, setSelectedStudent] = useState("");
  const [filterLevel, setFilterLevel] = useState("");
  const [filterStudent, setFilterStudent] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isHomeworkModalOpen, setIsHomeworkModalOpen] = useState(false);
  const [selectedHomeworkData, setSelectedHomeworkData] = useState({
    files: [],
    studentName: "",
    className: ""
  });
  
  // 알림톡 설정 상태
  const [notificationSettings, setNotificationSettings] = useState({
    classNoticeEnabled: false,
    classNoticeTemplate: "",
    classNoticeSendToStudent: false,
    classNoticeSendToParent: true,
    homeworkAssignedEnabled: false,
    homeworkAssignedTemplate: "",
    homeworkAssignedSendToStudent: false,
    homeworkAssignedSendToParent: true,
    gradingCompletedEnabled: false,
    gradingCompletedTemplate: "",
    gradingCompletedSendToStudent: false,
    gradingCompletedSendToParent: true
  });
  const [templates, setTemplates] = useState([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [macroModal, setMacroModal] = useState({ isOpen: false });
  const [exampleModal, setExampleModal] = useState({ isOpen: false });
  const [hasNotificationChanges, setHasNotificationChanges] = useState(false);
  const [savingNotificationSettings, setSavingNotificationSettings] = useState(false);
  const [expandedClassContent, setExpandedClassContent] = useState({});
  const [classContentModal, setClassContentModal] = useState({ isOpen: false, record: null });
  const [isQRGeneratorOpen, setIsQRGeneratorOpen] = useState(false);
  const [isQRUploadOpen, setIsQRUploadOpen] = useState(false);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  const formatGrade = (grade) => {
    const gradeNum = parseInt(grade);
    if (gradeNum >= 1 && gradeNum <= 6) {
      return `초등 ${gradeNum}학년`;
    } else if (gradeNum >= 7 && gradeNum <= 9) {
      return `중등 ${gradeNum - 6}학년`;
    } else if (gradeNum >= 10 && gradeNum <= 12) {
      return `고등 ${gradeNum - 9}학년`;
    }
    return `${grade}학년`;
  };

  const getSubmissionSummary = (homeworkFiles) => {
    if (homeworkFiles.length === 0) {
      return { submitted: 0, total: 0, graded: 0 };
    }

    const submitted = homeworkFiles.filter(file => file.student_submission).length;
    const graded = homeworkFiles.filter(file =>
      file.student_submission && file.student_submission.score !== null
    ).length;

    return {
      submitted,
      total: homeworkFiles.length,
      graded
    };
  };

  // Pagination calculations
  const totalRecords = paperAssignmentRecords.length;
  const totalPages = Math.ceil(totalRecords / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentRecords = paperAssignmentRecords.slice(startIndex, endIndex);

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [filterLevel, filterStudent]);

  // 템플릿 로드 함수 (알림톡 관리와 동일한 방식)
  const loadTemplates = async () => {
    setLoadingTemplates(true);
    try {
      console.log('=== 지필 과제: 센드온과 동기화 시작 ===');
      
      // 센드온과 동기화 먼저 수행
      console.log('1. 센드온 동기화 API 호출 중...');
      const syncResponse = await notificationAPI.syncTemplatesFromSendon();
      console.log('센드온 동기화 응답:', syncResponse);
      
      // 동기화 후 최신 템플릿 목록 가져오기
      console.log('2. 최신 템플릿 목록 조회 중...');
      const response = await notificationAPI.getAlimtalkTemplates();
      const data = response.data || response;
      setTemplates(Array.isArray(data) ? data : []);
      
      // 동기화 결과 로깅
      if (syncResponse && syncResponse.message) {
        console.log(`동기화 성공: ${syncResponse.message}`);
      }
      
      console.log(`총 ${Array.isArray(data) ? data.length : 0}개 템플릿을 로드했습니다.`);
      console.log('로드된 템플릿들:', data);
      
    } catch (error) {
      console.error('센드온 동기화 실패:', error);
      const errorMsg = error.response?.data?.error || error.message || '동기화 중 오류가 발생했습니다.';
      
      // 동기화 실패시에도 기존 템플릿 로드 시도
      try {
        console.log('3. 동기화 실패, 기존 템플릿만 로드 시도...');
        const response = await notificationAPI.getAlimtalkTemplates();
        const data = response.data || response;
        setTemplates(Array.isArray(data) ? data : []);
        
        console.log(`동기화 실패 (${errorMsg}), 하지만 기존 템플릿 ${Array.isArray(data) ? data.length : 0}개 로드됨`);
        console.log('기존 템플릿들:', data);
        
      } catch (fallbackError) {
        console.error('기존 템플릿 로드도 실패:', fallbackError);
        setTemplates([]);
        
        // 콘솔에만 로그 출력
        console.error(`템플릿 동기화 실패: ${errorMsg}`);
      }
    } finally {
      setLoadingTemplates(false);
    }
  };

  // 알림톡 설정 토글 함수
  const handleNotificationToggle = (field) => {
    setNotificationSettings(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
    setHasNotificationChanges(true);
  };

  // 템플릿 선택 함수
  const handleTemplateSelect = (field, templateId) => {
    setNotificationSettings(prev => ({
      ...prev,
      [field]: templateId
    }));
    setHasNotificationChanges(true);
  };

  // 알림톡 설정 저장 함수
  const saveNotificationSettings = async () => {
    setSavingNotificationSettings(true);
    try {
      await apiClient.post('/api/v1/paper-assignment/notification-settings/', {
        class_notice_enabled: notificationSettings.classNoticeEnabled,
        class_notice_template: notificationSettings.classNoticeTemplate,
        class_notice_send_to_student: notificationSettings.classNoticeSendToStudent,
        class_notice_send_to_parent: notificationSettings.classNoticeSendToParent,
        homework_assigned_enabled: notificationSettings.homeworkAssignedEnabled,
        homework_assigned_template: notificationSettings.homeworkAssignedTemplate,
        homework_assigned_send_to_student: notificationSettings.homeworkAssignedSendToStudent,
        homework_assigned_send_to_parent: notificationSettings.homeworkAssignedSendToParent,
        grading_completed_enabled: notificationSettings.gradingCompletedEnabled,
        grading_completed_template: notificationSettings.gradingCompletedTemplate,
        grading_completed_send_to_student: notificationSettings.gradingCompletedSendToStudent,
        grading_completed_send_to_parent: notificationSettings.gradingCompletedSendToParent
      }, { withAuth: true });

      alert('알림톡 설정이 저장되었습니다.');
      setHasNotificationChanges(false);
    } catch (error) {
      console.error('알림톡 설정 저장 실패:', error);
      const errorMsg = error.response?.data?.error || error.message || '설정 저장에 실패했습니다.';
      alert(`알림톡 설정 저장에 실패했습니다: ${errorMsg}`);
    } finally {
      setSavingNotificationSettings(false);
    }
  };

  // 알림톡 설정 불러오기 함수
  const loadNotificationSettings = async () => {
    try {
      const response = await apiClient.get('/api/v1/paper-assignment/notification-settings/', { withAuth: true });
      const data = response.data;

      setNotificationSettings({
        classNoticeEnabled: data.class_notice_enabled || false,
        classNoticeTemplate: data.class_notice_template || '',
        classNoticeSendToStudent: data.class_notice_send_to_student !== undefined ? data.class_notice_send_to_student : false,
        classNoticeSendToParent: data.class_notice_send_to_parent !== undefined ? data.class_notice_send_to_parent : true,
        homeworkAssignedEnabled: data.homework_assigned_enabled || false,
        homeworkAssignedTemplate: data.homework_assigned_template || '',
        homeworkAssignedSendToStudent: data.homework_assigned_send_to_student !== undefined ? data.homework_assigned_send_to_student : false,
        homeworkAssignedSendToParent: data.homework_assigned_send_to_parent !== undefined ? data.homework_assigned_send_to_parent : true,
        gradingCompletedEnabled: data.grading_completed_enabled || false,
        gradingCompletedTemplate: data.grading_completed_template || '',
        gradingCompletedSendToStudent: data.grading_completed_send_to_student !== undefined ? data.grading_completed_send_to_student : false,
        gradingCompletedSendToParent: data.grading_completed_send_to_parent !== undefined ? data.grading_completed_send_to_parent : true
      });
    } catch (error) {
      console.error('알림톡 설정 불러오기 실패:', error);
    }
  };


  // 클립보드 복사 함수
  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      alert('템플릿이 클립보드에 복사되었습니다!');
    } catch (error) {
      console.error('복사 실패:', error);
      alert('복사에 실패했습니다. 브라우저가 클립보드 접근을 지원하지 않습니다.');
    }
  };

  // 센드온에 템플릿 생성 함수
  const createTemplateInSendon = async (template) => {
    try {
      const response = await notificationAPI.createFromExample({
        template_name: template.title,
        template_content: template.content,
        trigger_type: getTriggerTypeFromTitle(template.title)
      });
      
      const actualData = response.data || response;
      
      if (actualData.success || actualData.message) {
        alert(`센드온에 템플릿이 생성되었습니다! ${actualData.message || '생성 완료'}`);
        loadTemplates();
      } else {
        alert('템플릿 생성에 실패했습니다.');
      }
    } catch (error) {
      console.error('템플릿 생성 실패:', error);
      const errorMsg = error.response?.data?.error || error.message || '알 수 없는 오류가 발생했습니다.';
      alert(`템플릿 생성 실패: ${errorMsg}`);
    }
  };

  // 트리거 타입 결정 함수
  const getTriggerTypeFromTitle = (title) => {
    if (title.includes('알림장')) return 'class_notice';
    if (title.includes('과제 배정') || title.includes('과제 부여')) return 'assignment_created';
    if (title.includes('채점 완료') || title.includes('완료')) return 'completion_notification';
    return 'assignment_created';
  };

  useEffect(() => {
    dispatch(fetchLevels());
    dispatch(fetchAssignments());
    dispatch(fetchPaperAssignmentRecords());
    dispatch(fetchTeacherStudentsOnlineAssignments({ isFinished: true }))
      .then((result) => {
        if (result.payload) {
          setFinishedOnlineAssignments(result.payload);
        }
      });
    loadTemplates();
    loadNotificationSettings();
  }, [dispatch]);

  useEffect(() => {
    // 레벨이 선택되면 해당 레벨의 학생들 가져오기
    if (selectedLevel) {
      dispatch(fetchStudents(selectedLevel));
    } else {
      dispatch(clearStudents());
    }
    setSelectedStudent(""); // 레벨 변경시 학생 선택 초기화
  }, [selectedLevel, dispatch]);

  // 에러 처리
  useEffect(() => {
    if (error) {
      alert(`오류: ${error}`);
      dispatch(clearError());
    }
  }, [error, dispatch]);


  const handleQrCodeGeneration = async () => {
    if (!selectedLevel || !selectedStudent) {
      alert("클래스와 학생을 모두 선택해주세요");
      return;
    }
    
    try {
      const result = await dispatch(generateQRCode({
        level_id: selectedLevel,
        student_id: selectedStudent
      }));
      
      if (generateQRCode.fulfilled.match(result)) {
        alert("QR 코드가 생성되었습니다!");
      }
    } catch (error) {
      console.error('Error generating QR code:', error);
    }
  };

  const handleQrCodeDownload = async () => {
    if (!selectedLevel || !selectedStudent) {
      alert("클래스와 학생을 모두 선택해주세요");
      return;
    }
    
    try {
      const result = await dispatch(downloadQRPdf({
        level_id: selectedLevel,
        student_id: selectedStudent
      }));
      
      if (downloadQRPdf.fulfilled.match(result)) {
        // Blob으로 PDF를 새 창에서 열기
        const response = result.payload;
        const blob = new Blob([response.data], { type: 'application/pdf' });
        const url = window.URL.createObjectURL(blob);
        
        // 새 창에서 PDF 열기
        const newWindow = window.open(url, '_blank');
        if (newWindow) {
          newWindow.focus();
        } else {
          alert("팝업이 차단되었습니다. 팝업을 허용해주세요.");
        }
        
        // 메모리 정리 (약간의 지연 후)
        setTimeout(() => {
          window.URL.revokeObjectURL(url);
        }, 1000);
      }
    } catch (error) {
      console.error('Error downloading PDF:', error);
      alert("PDF 다운로드 중 오류가 발생했습니다.");
    }
  };

  const handleAssignmentDownload = async (s3Key) => {
    try {
      const result = await dispatch(downloadAssignment(s3Key));
      
      if (downloadAssignment.fulfilled.match(result)) {
        // S3 presigned URL로 다운로드
        const { download_url, filename } = result.payload;
        const link = document.createElement('a');
        link.href = download_url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    } catch (error) {
      console.error('Error downloading assignment:', error);
    }
  };

  const handleViewHomework = (record) => {
    setSelectedHomeworkData({
      files: record.homework_files,
      studentName: record.student.name,
      className: record.level.name
    });
    setIsHomeworkModalOpen(true);
  };

  const handleDeleteRecord = async (recordId, studentName) => {
    if (window.confirm(`${studentName} 학생의 지필 과제 기록을 삭제하시겠습니까?\n\n이 작업은 되돌릴 수 없으며, 관련된 모든 파일도 함께 삭제됩니다.`)) {
      try {
        const result = await dispatch(deletePaperAssignmentRecord(recordId));
        
        if (deletePaperAssignmentRecord.fulfilled.match(result)) {
          alert("지필 과제 기록이 성공적으로 삭제되었습니다.");
        }
      } catch (error) {
        console.error('Error deleting record:', error);
        alert("삭제 중 오류가 발생했습니다.");
      }
    }
  };

  return (
    <div
      style={{
        padding: "10px",
        maxWidth: "1400px",
        margin: "0 auto",
      }}
    >
      {/* Header */}
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <h3 style={{ marginRight: "auto" }}>알림장 및 과제 관리</h3>
      </div>

      <div
        style={{
          backgroundColor: "#FFF",
          paddingBottom: "5px",
          marginTop: "10px",
        }}
      >
        {/* Class Notice and Assignment Section */}
        <div style={{ padding: "20px", borderBottom: "1px solid #EBEBEB", textAlign: "center" }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '15px', justifyContent: 'center', marginTop: '30px' }}>
            <div>
              <button
                onClick={() => setIsModalOpen(true)}
                style={{
                  padding: "20px 40px",
                  fontSize: "20px",
                  fontWeight: "bold",
                  borderRadius: "10px",
                  backgroundColor: "#11346A",
                  border: "none",
                  color: "#FFF",
                  cursor: "pointer",
                  boxShadow: "0 4px 12px rgba(17, 52, 106, 0.2)",
                  transition: "all 0.3s ease"
                }}
                onMouseOver={(e) => {
                  e.target.style.backgroundColor = "#0F2A5A";
                  e.target.style.transform = "translateY(-2px)";
                }}
                onMouseOut={(e) => {
                  e.target.style.backgroundColor = "#11346A";
                  e.target.style.transform = "translateY(0)";
                }}
              >
                알림장 및 과제 발송
              </button>
            </div>
            <div style={{ display: 'flex', flexDirection: 'row', gap: '15px', justifyContent: 'center' }}>
              <button
                onClick={() => {
                  dispatch(fetchLevels());
                  setIsQRGeneratorOpen(true);
                }}
                style={{
                  padding: "20px 40px",
                  fontSize: "20px",
                  fontWeight: "bold",
                  borderRadius: "10px",
                  backgroundColor: "#28a745",
                  border: "none",
                  color: "#FFF",
                  cursor: "pointer",
                  boxShadow: "0 4px 12px rgba(40, 167, 69, 0.2)",
                  transition: "all 0.3s ease"
                }}
                onMouseOver={(e) => {
                  e.target.style.backgroundColor = "#218838";
                  e.target.style.transform = "translateY(-2px)";
                }}
                onMouseOut={(e) => {
                  e.target.style.backgroundColor = "#28a745";
                  e.target.style.transform = "translateY(0)";
                }}
              >
                QR 표지 생성
              </button>
              <button
                onClick={() => setIsQRUploadOpen(true)}
                style={{
                  padding: "20px 40px",
                  fontSize: "20px",
                  fontWeight: "bold",
                  borderRadius: "10px",
                  backgroundColor: "#17a2b8",
                  border: "none",
                  color: "#FFF",
                  cursor: "pointer",
                  boxShadow: "0 4px 12px rgba(23, 162, 184, 0.2)",
                  transition: "all 0.3s ease"
                }}
                onMouseOver={(e) => {
                  e.target.style.backgroundColor = "#138496";
                  e.target.style.transform = "translateY(-2px)";
                }}
                onMouseOut={(e) => {
                  e.target.style.backgroundColor = "#17a2b8";
                  e.target.style.transform = "translateY(0)";
                }}
              >
                QR 코드 포함 파일 업로드
              </button>
            </div>
          </div>
          <p style={{
            marginTop: "15px",
            color: "#666",
            fontSize: "14px",
            maxWidth: "700px",
            margin: "15px auto 0",
            marginBottom: "30px",
          }}>
            학생별로 출결, 수업 태도, 코멘트, 숙제를 관리하고 발송할 수 있습니다.<br />QR 표지를 생성하고 스캔된 파일을 업로드하여 자동 분류하세요.
          </p>
        </div>

        {/* 알림톡 설정 Section */}
        <NotificationSettings 
          notificationSettings={notificationSettings}
          templates={templates}
          loadingTemplates={loadingTemplates}
          hasNotificationChanges={hasNotificationChanges}
          savingNotificationSettings={savingNotificationSettings}
          onNotificationToggle={handleNotificationToggle}
          onTemplateSelect={handleTemplateSelect}
          onLoadTemplates={loadTemplates}
          onSaveSettings={saveNotificationSettings}
          onShowMacroModal={() => setMacroModal({ isOpen: true })}
          onShowExampleModal={() => setExampleModal({ isOpen: true })}
        />

        {/* Assignment Management Table */}
        <div style={{ padding: "20px" }}>
          <h4 style={{ marginBottom: "15px", color: "#333" }}>수업 결과 및 지필 과제 목록</h4>
          
          {/* Filter Section */}
          <div style={{ display: "flex", gap: "10px", marginBottom: "20px", flexWrap: "wrap", alignItems: "center" }}>
            <select
              value={filterLevel}
              onChange={(e) => {
                setFilterLevel(e.target.value);
                setFilterStudent("");
                if (e.target.value) {
                  dispatch(fetchStudents(e.target.value));
                }
                dispatch(fetchAssignments(e.target.value || null));
                dispatch(fetchPaperAssignmentRecords({ levelId: e.target.value || null }));
                dispatch(fetchTeacherStudentsOnlineAssignments({ levelId: e.target.value || null, isFinished: true }))
                  .then((result) => {
                    if (result.payload) {
                      setFinishedOnlineAssignments(result.payload);
                    }
                  });
              }}
              style={{ 
                padding: "8px", 
                border: "1px solid #EBEBEB", 
                borderRadius: "5px",
                minWidth: "150px",
                fontSize: "16px"
              }}
            >
              <option value="">전체 클래스</option>
              {levels.map(level => (
                <option key={level.id} value={level.id}>{level.name}</option>
              ))}
            </select>
            
            <select
              value={filterStudent}
              onChange={(e) => {
                setFilterStudent(e.target.value);
                dispatch(fetchPaperAssignmentRecords({
                  levelId: filterLevel || null,
                  studentId: e.target.value || null
                }));
                dispatch(fetchTeacherStudentsOnlineAssignments({
                  levelId: filterLevel || null,
                  studentId: e.target.value || null,
                  isFinished: true
                }))
                  .then((result) => {
                    if (result.payload) {
                      setFinishedOnlineAssignments(result.payload);
                    }
                  });
              }}
              disabled={!filterLevel}
              style={{ 
                padding: "8px", 
                border: "1px solid #EBEBEB", 
                borderRadius: "5px",
                minWidth: "150px",
                fontSize: "16px",
                backgroundColor: !filterLevel ? "#f5f5f5" : "white"
              }}
            >
              <option value="">전체 학생</option>
              {students.map(student => (
                <option key={student.id} value={student.id}>
                  {student.name} ({formatGrade(student.grade)})
                </option>
              ))}
            </select>
            
            <span style={{ color: "#737373", fontSize: "14px" }}>
              * 클래스 또는 학생을 선택하지 않으면 전체 과제를 조회합니다.
            </span>
          </div>
          
          <table
            style={{
              width: "100%",
              borderCollapse: "collapse",
            }}
          >
            <colgroup>
              <col style={{ width: "10%" }} />
              <col style={{ width: "10%" }} />
              <col style={{ width: "10%" }} />
              <col style={{ width: "7%" }} />
              <col style={{ width: "7%" }} />
              <col style={{ width: "20%" }} />
              <col style={{ width: "15%" }} />
              <col style={{ width: "16%" }} />
              <col style={{ width: "10%" }} />
            </colgroup>
            <tbody>
              <tr
                style={{
                  textAlign: "left",
                  borderBottom: "1px solid #EBEBEB",
                  fontSize: "16px",
                  color: "#737373",
                  backgroundColor: "#f8f9fa"
                }}
              >
                <td style={{ padding: "12px", fontWeight: "bold" }}>날짜</td>
                <td style={{ padding: "12px", fontWeight: "bold" }}>학생</td>
                <td style={{ padding: "12px", fontWeight: "bold" }}>클래스</td>
                <td style={{ padding: "12px", fontWeight: "bold" }}>출석</td>
                <td style={{ padding: "12px", fontWeight: "bold" }}>태도</td>
                <td style={{ padding: "12px", fontWeight: "bold" }}>알림장 및 코멘트</td>
                <td style={{ padding: "12px", fontWeight: "bold", textAlign: "center" }}>과제 파일</td>
                <td style={{ padding: "12px", fontWeight: "bold", textAlign: "center" }}>제출 현황</td>
                <td style={{ padding: "12px", fontWeight: "bold", textAlign: "center" }}>액션</td>
              </tr>
              {currentRecords.length === 0 ? (
                <tr>
                  <td
                    colSpan="10"
                    style={{
                      padding: "40px",
                      textAlign: "center",
                      color: "#737373",
                      fontSize: "16px"
                    }}
                  >
                    {paperAssignmentRecords.length === 0
                      ? "아직 생성된 지필 과제 기록이 없습니다."
                      : "해당 페이지에 표시할 기록이 없습니다."}
                  </td>
                </tr>
              ) : (
                currentRecords.map((record, index) => {
                  const submissionSummary = getSubmissionSummary(record.homework_files);
                  return (
                    <tr
                      key={record.id}
                      style={{
                        fontSize: "14px",
                        borderBottom: "1px solid #EBEBEB"
                      }}
                    >
                      <td style={{ padding: "12px" }}>
                        <div style={{ fontWeight: "bold", color: "#000", fontSize: "14px" }}>
                          {new Date(record.class_date || record.created_at).toLocaleString('ko-KR', {
                            year: 'numeric',
                            month: '2-digit',
                            day: '2-digit',
                            hour: '2-digit',
                            minute: '2-digit',
                            hour12: false
                          })}
                        </div>
                      </td>
                      <td style={{ padding: "12px" }}>
                        <div style={{ fontWeight: "bold" }}>{record.student.name}</div>
                        <div style={{ fontSize: "12px", color: "#666" }}>
                          {formatGrade(record.student.grade)}
                        </div>
                      </td>
                      <td style={{ padding: "12px" }}>{record.level.name}</td>
                      <td style={{ padding: "12px" }}>
                        {record.attendance !== null ? (
                          <span style={{
                            padding: "4px 8px",
                            borderRadius: "4px",
                            fontSize: "12px",
                            fontWeight: "bold",
                            backgroundColor: record.attendance ? "#d4edda" : "#f8d7da",
                            color: record.attendance ? "#155724" : "#721c24"
                          }}>
                            {record.attendance ? "출석" : "결석"}
                          </span>
                        ) : (
                          <span style={{ color: "#999", fontSize: "12px" }}>-</span>
                        )}
                      </td>
                      <td style={{ padding: "12px" }}>
                        {record.attitude ? (
                          <span style={{
                            padding: "4px 8px",
                            borderRadius: "4px",
                            fontSize: "12px",
                            fontWeight: "bold",
                            backgroundColor:
                              record.attitude === "상" ? "#d4edda" :
                              record.attitude === "중" ? "#fff3cd" : "#f8d7da",
                            color:
                              record.attitude === "상" ? "#155724" :
                              record.attitude === "중" ? "#856404" : "#721c24"
                          }}>
                            {record.attitude}
                          </span>
                        ) : (
                          <span style={{ color: "#999", fontSize: "12px" }}>-</span>
                        )}
                      </td>
                      <td style={{ padding: "12px" }}>
                        {(() => {
                          let classContent = record.class_content;

                          // class_content가 문자열인 경우 JSON 파싱
                          if (typeof classContent === 'string') {
                            try {
                              classContent = JSON.parse(classContent);
                            } catch (error) {
                              console.warn('Failed to parse class_content:', error);
                              classContent = [];
                            }
                          }

                          const hasContent = classContent && Array.isArray(classContent) && classContent.length > 0;
                          const hasComment = record.comment && record.comment.trim() !== '';
                          const fileCount = hasContent ? classContent.filter(c => c.file_url).length : 0;

                          if (!hasContent && !hasComment) {
                            return <span style={{ color: "#666", fontSize: "12px" }}>내용 없음</span>;
                          }

                          return (
                            <button
                              onClick={() => setClassContentModal({ isOpen: true, record })}
                              style={{
                                padding: "6px 12px",
                                fontSize: "13px",
                                fontWeight: "500",
                                borderRadius: "5px",
                                backgroundColor: "#17a2b8",
                                border: "none",
                                color: "#FFF",
                                cursor: "pointer",
                                transition: "background-color 0.2s"
                              }}
                              onMouseOver={(e) => e.target.style.backgroundColor = "#138496"}
                              onMouseOut={(e) => e.target.style.backgroundColor = "#17a2b8"}
                            >
                              {hasContent && hasComment
                                ? `알림장 (${classContent.length}개${fileCount > 0 ? `, 파일 ${fileCount}개` : ''}) 및 코멘트 보기`
                                : hasContent
                                ? `알림장 (${classContent.length}개${fileCount > 0 ? `, 파일 ${fileCount}개` : ''}) 보기`
                                : '코멘트 보기'}
                            </button>
                          );
                        })()}
                      </td>
                      <td style={{ padding: "12px", textAlign: "center" }}>
                        {record.homework_files.length === 0 ? (
                          <span style={{ color: "#666", fontSize: "12px" }}>파일 없음</span>
                        ) : (
                          <button
                            onClick={() => handleViewHomework(record)}
                            style={{
                              padding: "6px 12px",
                              fontSize: "13px",
                              fontWeight: "500",
                              borderRadius: "5px",
                              backgroundColor: "#11346A",
                              border: "none",
                              color: "#FFF",
                              cursor: "pointer",
                              transition: "background-color 0.2s"
                            }}
                            onMouseOver={(e) => e.target.style.backgroundColor = "#0F2A5A"}
                            onMouseOut={(e) => e.target.style.backgroundColor = "#11346A"}
                          >
                            과제 보기 ({record.homework_files.length}개)
                          </button>
                        )}
                      </td>
                      <td style={{ padding: "12px", textAlign: "center" }}>
                        {record.homework_files.length === 0 ? (
                          <span style={{ color: "#666", fontSize: "12px" }}>-</span>
                        ) : (
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', justifyContent: 'center' }}>
                            {/* 제출 상황 */}
                            <span style={{
                              padding: '2px 6px',
                              borderRadius: '10px',
                              fontSize: '11px',
                              fontWeight: 'bold',
                              backgroundColor: submissionSummary.submitted === submissionSummary.total ? '#d4edda' : 
                                             submissionSummary.submitted > 0 ? '#fff3cd' : '#f8d7da',
                              color: submissionSummary.submitted === submissionSummary.total ? '#155724' : 
                                    submissionSummary.submitted > 0 ? '#856404' : '#721c24'
                            }}>
                              제출 {submissionSummary.submitted}/{submissionSummary.total}
                            </span>
                            
                            {/* 채점 상황 */}
                            {submissionSummary.submitted > 0 && (
                              <span style={{
                                padding: '2px 6px',
                                borderRadius: '10px',
                                fontSize: '11px',
                                fontWeight: 'bold',
                                backgroundColor: submissionSummary.graded === submissionSummary.submitted ? '#d4edda' : '#fff3cd',
                                color: submissionSummary.graded === submissionSummary.submitted ? '#155724' : '#856404'
                              }}>
                                채점 {submissionSummary.graded}/{submissionSummary.submitted}
                              </span>
                            )}
                          </div>
                        )}
                      </td>
                      <td style={{ padding: "12px", textAlign: "center" }}>
                        <button
                          onClick={() => handleDeleteRecord(record.id, record.student.name)}
                          style={{
                            padding: "4px 8px",
                            fontSize: "12px",
                            fontWeight: "500",
                            borderRadius: "4px",
                            backgroundColor: "#dc3545",
                            border: "none",
                            color: "#FFF",
                            cursor: "pointer",
                            transition: "background-color 0.2s"
                          }}
                          onMouseOver={(e) => e.target.style.backgroundColor = "#c82333"}
                          onMouseOut={(e) => e.target.style.backgroundColor = "#dc3545"}
                          title="이 기록을 삭제합니다"
                        >
                          삭제
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>

          {/* Pagination Controls */}
          {totalRecords > 0 && (
            <div style={{
              marginTop: "20px",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexWrap: "wrap",
              gap: "15px"
            }}>
              {/* Items per page selector */}
              <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                <label style={{ fontSize: "14px", color: "#666" }}>페이지당 항목:</label>
                <select
                  value={itemsPerPage}
                  onChange={(e) => {
                    setItemsPerPage(Number(e.target.value));
                    setCurrentPage(1);
                  }}
                  style={{
                    padding: "6px 10px",
                    border: "1px solid #EBEBEB",
                    borderRadius: "5px",
                    fontSize: "14px",
                    cursor: "pointer"
                  }}
                >
                  <option value={5}>5개</option>
                  <option value={10}>10개</option>
                  <option value={20}>20개</option>
                  <option value={50}>50개</option>
                  <option value={100}>100개</option>
                </select>
              </div>

              {/* Page info and navigation */}
              <div style={{ display: "flex", alignItems: "center", gap: "15px" }}>
                <span style={{ fontSize: "14px", color: "#666" }}>
                  {startIndex + 1}-{Math.min(endIndex, totalRecords)} / 총 {totalRecords}개
                </span>

                <div style={{ display: "flex", gap: "5px" }}>
                  {/* First page button */}
                  <button
                    onClick={() => setCurrentPage(1)}
                    disabled={currentPage === 1}
                    style={{
                      padding: "6px 12px",
                      fontSize: "14px",
                      borderRadius: "5px",
                      backgroundColor: currentPage === 1 ? "#f5f5f5" : "#FFF",
                      border: "1px solid #EBEBEB",
                      color: currentPage === 1 ? "#999" : "#333",
                      cursor: currentPage === 1 ? "not-allowed" : "pointer",
                      transition: "all 0.2s"
                    }}
                    onMouseOver={(e) => {
                      if (currentPage !== 1) {
                        e.target.style.backgroundColor = "#f5f5f5";
                      }
                    }}
                    onMouseOut={(e) => {
                      if (currentPage !== 1) {
                        e.target.style.backgroundColor = "#FFF";
                      }
                    }}
                  >
                    ⟪
                  </button>

                  {/* Previous page button */}
                  <button
                    onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                    disabled={currentPage === 1}
                    style={{
                      padding: "6px 12px",
                      fontSize: "14px",
                      borderRadius: "5px",
                      backgroundColor: currentPage === 1 ? "#f5f5f5" : "#FFF",
                      border: "1px solid #EBEBEB",
                      color: currentPage === 1 ? "#999" : "#333",
                      cursor: currentPage === 1 ? "not-allowed" : "pointer",
                      transition: "all 0.2s"
                    }}
                    onMouseOver={(e) => {
                      if (currentPage !== 1) {
                        e.target.style.backgroundColor = "#f5f5f5";
                      }
                    }}
                    onMouseOut={(e) => {
                      if (currentPage !== 1) {
                        e.target.style.backgroundColor = "#FFF";
                      }
                    }}
                  >
                    ‹
                  </button>

                  {/* Page numbers */}
                  {(() => {
                    const pageNumbers = [];
                    const maxVisiblePages = 5;
                    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
                    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

                    if (endPage - startPage < maxVisiblePages - 1) {
                      startPage = Math.max(1, endPage - maxVisiblePages + 1);
                    }

                    for (let i = startPage; i <= endPage; i++) {
                      pageNumbers.push(
                        <button
                          key={i}
                          onClick={() => setCurrentPage(i)}
                          style={{
                            padding: "6px 12px",
                            fontSize: "14px",
                            fontWeight: i === currentPage ? "bold" : "normal",
                            borderRadius: "5px",
                            backgroundColor: i === currentPage ? "#11346A" : "#FFF",
                            border: i === currentPage ? "1px solid #11346A" : "1px solid #EBEBEB",
                            color: i === currentPage ? "#FFF" : "#333",
                            cursor: "pointer",
                            minWidth: "40px",
                            transition: "all 0.2s"
                          }}
                          onMouseOver={(e) => {
                            if (i !== currentPage) {
                              e.target.style.backgroundColor = "#f5f5f5";
                            }
                          }}
                          onMouseOut={(e) => {
                            if (i !== currentPage) {
                              e.target.style.backgroundColor = "#FFF";
                            }
                          }}
                        >
                          {i}
                        </button>
                      );
                    }
                    return pageNumbers;
                  })()}

                  {/* Next page button */}
                  <button
                    onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                    disabled={currentPage === totalPages}
                    style={{
                      padding: "6px 12px",
                      fontSize: "14px",
                      borderRadius: "5px",
                      backgroundColor: currentPage === totalPages ? "#f5f5f5" : "#FFF",
                      border: "1px solid #EBEBEB",
                      color: currentPage === totalPages ? "#999" : "#333",
                      cursor: currentPage === totalPages ? "not-allowed" : "pointer",
                      transition: "all 0.2s"
                    }}
                    onMouseOver={(e) => {
                      if (currentPage !== totalPages) {
                        e.target.style.backgroundColor = "#f5f5f5";
                      }
                    }}
                    onMouseOut={(e) => {
                      if (currentPage !== totalPages) {
                        e.target.style.backgroundColor = "#FFF";
                      }
                    }}
                  >
                    ›
                  </button>

                  {/* Last page button */}
                  <button
                    onClick={() => setCurrentPage(totalPages)}
                    disabled={currentPage === totalPages}
                    style={{
                      padding: "6px 12px",
                      fontSize: "14px",
                      borderRadius: "5px",
                      backgroundColor: currentPage === totalPages ? "#f5f5f5" : "#FFF",
                      border: "1px solid #EBEBEB",
                      color: currentPage === totalPages ? "#999" : "#333",
                      cursor: currentPage === totalPages ? "not-allowed" : "pointer",
                      transition: "all 0.2s"
                    }}
                    onMouseOver={(e) => {
                      if (currentPage !== totalPages) {
                        e.target.style.backgroundColor = "#f5f5f5";
                      }
                    }}
                    onMouseOut={(e) => {
                      if (currentPage !== totalPages) {
                        e.target.style.backgroundColor = "#FFF";
                      }
                    }}
                  >
                    ⟫
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Calendar View */}
        <div style={{ padding: "20px" }}>
          <PaperAssignmentCalendar
            paperAssignmentRecords={paperAssignmentRecords}
            onlineAssignments={finishedOnlineAssignments || []}
          />
        </div>
      </div>

      {/* Class Notice Modal */}
      <ClassNoticeModal 
        isOpen={isModalOpen} 
        onClose={() => setIsModalOpen(false)}
        onSuccess={() => dispatch(fetchPaperAssignmentRecords())}
      />

      {/* Homework Files Modal */}
      <HomeworkFilesModal
        isOpen={isHomeworkModalOpen}
        onClose={() => setIsHomeworkModalOpen(false)}
        homeworkFiles={selectedHomeworkData.files}
        studentName={selectedHomeworkData.studentName}
        className={selectedHomeworkData.className}
        onUpdate={() => dispatch(fetchPaperAssignmentRecords())}
      />

      <MacroGuideModal 
        isOpen={macroModal.isOpen}
        onClose={() => setMacroModal({ isOpen: false })}
      />

      <ExampleTemplateModal 
        isOpen={exampleModal.isOpen}
        onClose={() => setExampleModal({ isOpen: false })}
        onCopyToClipboard={copyToClipboard}
        onCreateTemplate={createTemplateInSendon}
      />

      <ClassContentModal
        isOpen={classContentModal.isOpen}
        record={classContentModal.record}
        onClose={() => setClassContentModal({ isOpen: false, record: null })}
      />

      {/* QR Cover Generator Modal */}
      <QRCoverGenerator
        isOpen={isQRGeneratorOpen}
        onClose={() => setIsQRGeneratorOpen(false)}
        levels={levels}
        onGenerate={() => {
          // QR 생성 후 필요한 작업
        }}
      />

      {/* QR Bulk Upload Modal */}
      <QRBulkUploadModal
        isOpen={isQRUploadOpen}
        onClose={() => setIsQRUploadOpen(false)}
        onSuccess={() => dispatch(fetchPaperAssignmentRecords())}
      />
    </div>
  );
};

export default PaperAssignmentPage;
